/**
 * The palette from the prototype. Validated for lightness progression, contrast and colour
 * blindness, and carried over unchanged (specification 8.3).
 *
 * Two rules travel with it:
 *   - status colours are never used as series colours;
 *   - no value is conveyed by colour alone. Every chart is accompanied by a table of the
 *     same numbers.
 *
 * SEAL keeps this blue ramp — higher is better there, and blue reads as neutral-to-positive.
 * The dependency scale has its own ramp below: higher is worse on that one, and sharing a single
 * colour between two scales that point in opposite directions asked the reader to remember which
 * chart they were looking at before the colour meant anything.
 */
export const sequential = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'] as const;

/**
 * The dependency scale, 1-5: higher means deeper lock-in. Escalates blue → amber → dark orange
 * rather than green → red — red-green is the most common form of colour blindness, and this
 * still reads as rising severity without relying on that pair. Deliberately not the `status`
 * tokens: those are reserved for status badges, and reusing them here would blur "this is a
 * value on a chart" with "this is a status" (the rule above).
 */
export const dependencySeverity = ['#2e6e9e', '#4e9e8f', '#d9a441', '#d97b29', '#a83e17'] as const;

export const status = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const;

export const surface = { light: '#fcfcfb', dark: '#1a1a19' } as const;

export const ink = { primary: '#0b0b0b', secondary: '#52514e', muted: '#898781' } as const;

export const inkDark = { primary: '#f4f3f0', secondary: '#c3c1bb', muted: '#8d8b85' } as const;

/** Borders and dividers, kept out of the ink scale so they can be tuned independently. */
export const line = { light: '#e6e4df', dark: '#38372f' } as const;

/**
 * A trade-show stand is operated from an iPad. 44 px is the smallest touch target that is
 * reliably hittable while standing up and talking (specification 8.4).
 */
export const touchTargetMinimum = 44;

export type StatusTone = keyof typeof status;

/** Position on the 1-5 dependency ramp. Higher means more dependency. */
export function dependencyColour(score: number): string {
  const index = Math.min(Math.max(Math.round(score), 1), dependencySeverity.length) - 1;
  return dependencySeverity[index] ?? dependencySeverity[0];
}

/** Position on the 0-4 SEAL ramp. Higher means more sovereignty. */
export function sealColour(level: number): string {
  const index = Math.min(Math.max(Math.round(level), 0), sequential.length - 1);
  return sequential[index] ?? sequential[0];
}
